import React, { RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../translation/namespace';
import { ImageGallery, ImageState } from '../../../../types/api/ImageType';
import IForm from '../../../../types/IForm';
import { murmurhash } from '../../../../lib/js/hash';
import { SkipableContent } from '../SkipableContent';
import { ImageList } from '../ImageList';
import { Tooltip, TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import { ModalDialogModifier } from '../../../../components/organisms/ModalDialog/ModalDialog';
import { HiddenGalleryHeadline } from '../HiddenGalleryHeadline';
import { PopupSliderGallery } from './PopupSliderGallery';
import { ImageListItem } from '../ImageListItem';
import { useGlobalPrintMode } from '../../../../contexts/GlobalPrintMode';

export type Forms = {
  edit: IForm;
  delete: IForm;
};

export type GalleryImage = {
  // unclear linting error
  image: ImageGallery;
};

type GalleryProps = {
  id: string;
  images: GalleryImage[];
};

export const DISPLAY_TYPE = {
  PREVIEW_BEFORE_UPLOAD: 'PREVIEW_BEFORE_UPLOAD',
  PREVIEW_AFTER_UPLOAD: 'PREVIEW_AFTER_UPLOAD',
  SHOW_ERRORS: 'SHOW_ERRORS',
  IMAGE_GALLERY: 'IMAGE_GALLERY',
};

export default function ThumbnailGallery({ id, images }: GalleryProps) {
  const { t } = useTranslation(Namespace.ImageGallery);
  const displayableImages = images.filter(({ image }) => image.imageState !== ImageState.pending);
  const uniqueId = murmurhash(`${id}:imageGallery`);
  const popUpButtonRef = useRef(null);
  const [showModal, setShowModal] = useState<string | null>(null);
  const { isInGlobalPrintMode } = useGlobalPrintMode();

  /**
   * Necessary for empty image galleries
   * No images => no rendering.
   */
  if (displayableImages.length === 0) {
    return null;
  }

  return (
    <div className="image-gallery">
      <HiddenGalleryHeadline id={uniqueId}>{t('headlineThumbnailGallery')}</HiddenGalleryHeadline>
      <SkipableContent id={id}>
        <ImageList>
          {images.map(image => (
            <ImageListItem key={image.image.imageId}>
              <Tooltip attachTo={popUpButtonRef} placement={TooltipPlacement.TOP} hideTooltipFromScreenReader>
                <button
                  ref={popUpButtonRef}
                  type="button"
                  title={t('popUpImageButton', { fileName: image.image.originalFilename }) as string}
                  className="image-gallery__image-enlarge-button"
                  onClick={() => setShowModal(image.image.imageId)}
                >
                  {/* img alt can be empty, because button has the name */}
                  <img
                    src={image.image.src}
                    alt={t('popUpImageButton', { fileName: image.image.originalFilename }) as string}
                    width="320"
                    loading={isInGlobalPrintMode ? undefined : 'lazy'}
                    className="image-gallery__upload-preview"
                  />
                  <span className="image-gallery__image-enlarge-button-indicator">
                    <SVG svgSpriteId={Icons.EXPAND_ARROWS} svgClass="image-gallery__image-enlarge-button-svg" />
                  </span>
                </button>
              </Tooltip>
            </ImageListItem>
          ))}
        </ImageList>
      </SkipableContent>
      {showModal && (
        <ModalDialog
          closeModal={() => setShowModal(null)}
          headline={t('headlineModalWithSliderGallery')}
          modifier={[ModalDialogModifier.POP_UP, ModalDialogModifier.SLIDER_IN_POP_UP]}
        >
          {focusRef => (
            <PopupSliderGallery
              id={id}
              images={images}
              ref={focusRef as RefObject<HTMLLIElement>}
              focusedImageId={showModal}
            />
          )}
        </ModalDialog>
      )}
    </div>
  );
}
